import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { admin } from '~/components/Admin/AdminDialog/AdminDialog.style';
import { useDropzone } from 'react-dropzone';
import {
  UploadFileRounded,
  InsertDriveFileRounded,
  Replay,
  WarningRounded,
} from '@mui/icons-material';
import { pxToRem } from '~/theme/typography';
import {
  useUpdateInternetByFileMutation,
  useUpdateByFileMutation,
} from '~/api/oneBoxV3';
import { adminTab } from '~/types/enum';
import { BaseSnackbar } from '~/components/Global/Snackbar';
import { FileFieldText } from '~/views/Admin/Admin.constant';

type TProps = {
  handleClose: () => void;
  isLaunch: boolean;
};

export const UploadDialogContent = ({ handleClose, isLaunch }: TProps) => {
  const [searchParams] = useSearchParams();
  const [file, setFile] = useState<File[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | undefined>('');
  const [updateByFileApi, { isLoading }] = useUpdateByFileMutation();
  const [UpdateInternetByFileApi, { isLoading: isUpdateInternetLoading }] =
    useUpdateInternetByFileMutation();
  const [showSnackbar, setShowSnackbar] = useState({
    isShow: false,
    message: '',
  });

  const onDrop = (acceptedFiles: File[]) => {
    void (async () => {
      try {
        setFile(acceptedFiles);
        const formData = new FormData();
        formData.append('file', acceptedFiles[0]);
        formData.append('isLaunch', isLaunch.toString());
        const tab = searchParams.get('tab');
        let result;
        switch (tab) {
          case adminTab.InternetResource:
            result = await UpdateInternetByFileApi(formData).unwrap();
            break;
          default:
            result = await updateByFileApi(formData).unwrap();
        }

        if (result.err !== 0) {
          let errMsg = result.msg;
          switch (result.err) {
            case 4001: {
              const errorResourceColumns = result.data?.errorResourceColumns;
              const errorId = result.data?.errorId;
              errMsg = `ID ‘${errorId}’ ：欄位 ‘${errorResourceColumns?.map(item => FileFieldText[item as keyof typeof FileFieldText])?.join('’、‘')}’ 有誤`;
              break;
            }
            case 2003: {
              // 缺少欄位
              break;
            }
          }
          setErrorMsg(errMsg);
          return;
        }

        setShowSnackbar({
          message: '檔案已上傳成功！',
          isShow: true,
        });
        handleClose();
      } catch (error) {
        console.log('updateByFileApi error', error);
      }
    })();
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
    },
  });

  return (
    <>
      <admin.DialogContent>
        <admin.ContentContainer>
          <admin.FileName>請確認檔案格式為 xlsx</admin.FileName>
          {!(isLoading || isUpdateInternetLoading) && errorMsg == '' && (
            <admin.FileUploadContainer {...getRootProps()}>
              <UploadFileRounded sx={{ fontSize: pxToRem(40) }} />
              <input {...getInputProps()} />
              點擊選擇檔案
              <br />
              一次僅能選擇一個檔案
            </admin.FileUploadContainer>
          )}
          {/* Loading */}
          {(isLoading || isUpdateInternetLoading) && (
            <>
              <admin.FileInfoContent>
                <InsertDriveFileRounded />
                <admin.FileInfoText>{file[0].name}</admin.FileInfoText>
                <admin.FileInfoButton
                  color="inherit"
                  variant="outlined"
                  {...getRootProps()}
                >
                  <Replay />
                  重新選擇
                  <input {...getInputProps()} />
                </admin.FileInfoButton>
              </admin.FileInfoContent>
              <admin.FileLoadingBar>正在檢查資料 ...</admin.FileLoadingBar>
            </>
          )}
          {/* Error */}
          {!(isLoading || isUpdateInternetLoading) && errorMsg !== '' && (
            <>
              <admin.FileInfoContent>
                <InsertDriveFileRounded />
                <admin.FileInfoText>{file[0].name}</admin.FileInfoText>
                <admin.FileInfoButton
                  color="inherit"
                  variant="outlined"
                  {...getRootProps()}
                >
                  <Replay />
                  重新選擇
                  <input {...getInputProps()} />
                </admin.FileInfoButton>
              </admin.FileInfoContent>
              <admin.FileErrorContainer>
                <admin.FileErrorTitle>
                  <WarningRounded sx={{ color: '#FF4842' }} />
                  資料有誤，請修正後重新選擇檔案上傳！
                </admin.FileErrorTitle>
                <admin.FileErrorContent>
                  <admin.FileErrorItem>{errorMsg}</admin.FileErrorItem>
                </admin.FileErrorContent>
              </admin.FileErrorContainer>
            </>
          )}
        </admin.ContentContainer>
      </admin.DialogContent>
      <BaseSnackbar
        open={showSnackbar.isShow}
        message={showSnackbar.message}
        handleClose={() => setShowSnackbar({ isShow: false, message: '' })}
      />
    </>
  );
};
